import '@banno/jha-wc/src/forms/jha-form-select/jha-form-select';
// eslint-disable-next-line no-duplicate-imports
import JhaFormSelect from '@banno/jha-wc/src/forms/jha-form-select/jha-form-select';
import { SelectOption } from '@banno/jha-wc/src/forms/jha-form-select/JhaFormSelect';
import { createUniqueId } from '@jack-henry/frontend-utils/functions';
import { html, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { TmFormControl } from '../forms';

const tagName = 'tm-wc-select';

type LabeledItemVm<T> = T & {
    label: string;
};

const isLabelItemVm = (item: unknown): item is LabeledItemVm<unknown> => {
    if (typeof (item as LabeledItemVm<unknown>).label === 'string') {
        return true;
    }
    return false;
};

interface TmSelectOption<T> {
    vm: SelectOption;
    data: T;
}

function createMapOption<T>(option: LabeledItemVm<T>): TmSelectOption<T>;
function createMapOption<T>(option: T, labelFunction: (option: T) => string): TmSelectOption<T>;
function createMapOption<T>(
    option: T | LabeledItemVm<T>,
    labelFunction?: (option: T) => string
): TmSelectOption<T> {
    let name;
    if (isLabelItemVm(option)) {
        name = option.label;
    } else if (labelFunction) {
        name = labelFunction(option);
    } else {
        throw new Error('No labelFunction provided');
    }

    return {
        vm: {
            value: createUniqueId(),
            name,
            disabled: false,
            selected: false,
        },
        data: option,
    };
}

@customElement(tagName)
export class TmSelect<T> extends TmFormControl<T | SelectOption> {
    @property({ type: Array })
    public options?: ({ label: string; value: T } | SelectOption)[];

    // This property is the label of the combo box itself, not of the options in the select
    @property({ type: String })
    public label?: string;

    // This is a label function used to generate the label for each option in the select. It only uses this function if a label is not provided in the option object
    @property({ attribute: false })
    public labelFunction?: (option: T | SelectOption) => string;

    @property({ type: Boolean })
    public disabled?: boolean;

    protected verifiedPropNames: readonly (keyof this)[] = ['options'];

    @state()
    private selectOptions = new Map<string, TmSelectOption<T>>();

    private readonly selectInputRef = createRef<JhaFormSelect>();

    private get selectInput() {
        return this.selectInputRef.value;
    }

    public firstUpdated() {
        this.hydrateMap();
    }

    protected validate() {
        if (!this.selectInput) {
            return true;
        }

        return this.selectInput.checkValidity();
    }

    protected updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('options')) {
            this.hydrateMap();
        }
    }

    protected render() {
        const myOptions = Array.from(this.selectOptions.values()).map(option => option.vm);
        let initialValue = '';
        if (this.initial) {
            // Loop over this.selectOptions to find the key for the entry where the entry equals this.initial
            for (const [key, value] of this.selectOptions.entries()) {
                if (value.data === this.initial) {
                    initialValue = key;
                    break;
                }
            }
        }
        return html`
            <jha-form-select
                ${ref(this.selectInputRef)}
                .options=${myOptions}
                hasInteraction_
                @change=${(e: CustomEvent) => {
                    // Get option object from VM based on uuid
                    const value = this.selectOptions.get(e.detail);
                    if (value) {
                        this.changeValue(value.data);
                    }
                }}
                .label=${this.label ?? ''}
                .value=${initialValue}
                ?disabled=${this.disabled}
            ></jha-form-select>
        `;
    }

    private hydrateMap() {
        this.selectOptions = new Map();
        if (Array.isArray(this.options)) {
            this.options.forEach(option => {
                let item;
                if (isLabelItemVm(option)) {
                    item = createMapOption(option);
                } else {
                    if (!this.labelFunction) {
                        throw new Error('No labelFunction provided');
                    }
                    item = createMapOption(option, this.labelFunction);
                }
                this.selectOptions.set(item.vm.value, item as TmSelectOption<T>);
            });
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmSelect<unknown>;
    }
}
